import React from 'react';
import { animated, useSpring } from 'react-spring';
import { Formik, Form } from 'formik';
import { Global, css } from '@emotion/core';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from 'emotion-theming';

import AcceptAgreementField from '../../../registration/components/shared/AcceptAgreementField';
import Button from '../../../../shared/_TBComponents/Button';
import Checkmark from '../../../../shared/_TBComponents/Checkmark';
import CommonFields from '../../../registration/components/shared/CommonFields';
import encode from '../../../../shared/utils/encodeToUrl';
import { registerUser } from '../../../registration/ducks/registrationActions';
import { RegistrationSchemaReseller } from '../../../../shared/utils/registrationSchemaHelper';
import Spinner from '../../../../shared/_TBComponents/Spinner';
import useLogin from '../../../login/components/hooks/useLogin';

const FormGroup = styled('div')`
`;

const SpinnerContainer = styled('div')`
  display: flex;
  height: 100%;
  flex: 1;
  position: absolute;
  top: 0;
  justify-content: center;
  width: 100%;
  z-index: 0;
`;

const LoginContainer = styled('div')`
  position: relative;

  p {
    margin-bottom: 0px !important;
  }
`;

const ModalCreateAccount = ({
  openModal,
  resellerSlug,
}) => {
  const {
    isAuthenticated,
  } = useLogin();

  const isRegistrationLoading = useSelector((state) => state.registration.ui.isRegistrationLoading);

  const completeAnimation = useSpring({ opacity: isAuthenticated ? 1 : 0 });
  const formAnimation = useSpring({
    opacity: isRegistrationLoading || isAuthenticated ? 0 : 1,
    zIndex: 1,
    position: 'relative',
  });
  const spinnerAnimation = useSpring({ opacity: isRegistrationLoading ? 1 : 0, zIndex: 0 });

  const dispatch = useDispatch();

  const theme = useTheme();

  return (
    <Formik
      initialValues={{
        acceptAgreement: false,
        authenticationMethod: 'PASSWORD',
        client_id: process.env.CLIENT_ID,
        client_secret: process.env.CLIENT_SECRET,
        confirmedPassword: '',
        emailAddress: '',
        firstName: '',
        lastName: '',
        password: '',
        registrationType: 'EXISTING_ORGANIZATION',
        resellerSlug,
        scope: 'offline_access',
      }}
      onSubmit={(values) => {
        dispatch(registerUser(encode(values)));
      }}
      validationSchema={RegistrationSchemaReseller}
      render={({
        errors,
        touched,
        handleSubmit,
        values,
        setFieldValue,
      }) => (
        <LoginContainer>
          <Global
            styles={css`
              .ReactModal__Content {
                width: 500px!important;
              }
            `}
          />
          <>
            <animated.div style={formAnimation}>
              <Form
                name='login'
                id='login'
              >
                <CommonFields
                  errors={errors}
                  setFieldValue={setFieldValue}
                  showGenderField={false}
                  showOrgFields={false}
                  showPhoneFields={false}
                  touched={touched}
                  values={values}
                />
                <AcceptAgreementField
                  error={errors.acceptAgreement}
                  setFieldValue={setFieldValue}
                  showAgeNotice={false}
                  touched={touched.acceptAgreement}
                  values={{
                    acceptAgreement: values.acceptAgreement,
                  }}
                />
                <FormGroup className='text-center'>
                  <Button
                    bgColor={theme.colors.orange}
                    disabled={
                        errors.password
                        || errors.emailAddress
                        || errors.confirmedPassword
                        || errors.acceptAgreement
                        || isRegistrationLoading
                      }
                    fullWidth
                    isLoading={isRegistrationLoading}
                    large
                    lightSpinner
                    name='submit'
                    noBorder
                    onClick={handleSubmit}
                    rounded
                    txtColor={theme.colors.white}
                    upperCase
                  />
                </FormGroup>
                <Button
                  fontSize='14px'
                  fullWidth
                  large
                  name='Already have an account? Click here to log in.'
                  noBorder
                  onClick={() => openModal('login')}
                  styledWrapperProps={{ margin: '15px 0px 0px' }}
                  txtColor={theme.colors.lightText[1]}
                  type='button'
                  upperCase
                />
              </Form>
            </animated.div>
            <animated.div style={spinnerAnimation}>
              <SpinnerContainer>
                <Spinner />
              </SpinnerContainer>
            </animated.div>
            <animated.div style={completeAnimation}>
              <SpinnerContainer>
                <Checkmark />
              </SpinnerContainer>
            </animated.div>
          </>
        </LoginContainer>
      )}
    />
  );
};

ModalCreateAccount.propTypes = {
  openModal: PropTypes.func.isRequired,
  resellerSlug: PropTypes.string.isRequired,
};

export default ModalCreateAccount;
